import {
  h,
  defineAsyncComponent,
  mergeProps,
  resolveDynamicComponent,
  DefineComponent,
  VNode,
} from 'vue'
import { tableField } from './utils'
const Table = defineAsyncComponent(() => import('./VueTable.vue'))
// import {CCheckbox as ControlCheckbox } from '@/components/UiElements/Controls'

const FIELD_NAME = 'element-selection'

interface SelectionTableProps {
  fields: Record<string, unknown>[]
  items: Record<string, unknown>[]
  'aggregate-fields'?: Record<string, unknown>[]
  aggregate?: Record<string, unknown>[]
  selection?: any
  'table-component'?: DefineComponent
  keyGetter: (v: unknown) => unknown
  [key: string]: unknown
}
interface Context {
  slots: unknown
  attrs: Record<string, unknown>
  emit: unknown
}

/**
 * A function that renders a selection table component with customizable props and slots.
 *
 * @param props - An object containing the props for the selection table.
 * @param props.fields - An array of field objects to be displayed in the table.
 * @param props['aggregate-fields'] - An array of field objects for aggregate data.
 * @param props.selection - An optional selection object for managing table row selection.
 * @param props.items - An array of items to be displayed in the table.
 * @param props.aggregate - An array of aggregate data to be displayed in the table.
 * @param props['table-component'] - An optional custom table component to be used instead of the default.
 * @param props.keyGetter - A function to generate a unique key for each item in the table.
 * @param context - An object containing the context of the component.
 * @param context.slots - An object containing the slots for the component.
 * @param context.attrs - An object containing the attributes for the component.
 * @param context.emit - A function to emit events from the component.
 *
 * @returns A VNode representing the selection table component.
 */
export default function SelectionTable(
  props: SelectionTableProps,
  context: Context
): VNode {
  const {
    fields = [],
    'aggregate-fields': aggregateFields = [],
    selection = undefined,
    items = [],
    aggregate = [],
    'table-component': tableComponent = Table,
    keyGetter = (v: unknown): unknown => v,
  } = props
  const ControlCheckboxComponent = resolveDynamicComponent('control-checkbox')
  const slots = selection
    ? {
        [`head-cell-${FIELD_NAME}`]: () =>
          h(ControlCheckboxComponent as DefineComponent, {
            type: 'checkbox',
            key: selection.amountListed.value,
            checked:
              selection.amountListed.value ===
              selection.options.value.items.length,
            indeterminate:
              selection.amountListed.value !== 0 &&
              selection.amountListed.value !==
                selection.options.value.items.length,
            onChange: () => {
              selection.toggleAllListed(),
                selection.state.all.value ? selection.setAll(false) : 0
            },
          }),
        [`cell-${FIELD_NAME}`]: function cellSelection(
          { item }: { item: unknown } = { item: undefined }
        ) {
          return h(ControlCheckboxComponent as DefineComponent, {
            type: 'checkbox',
            key: selection.amountListed.value,
            checked: selection.isSelected(keyGetter(item)),
            onChange: () => {
              selection.update(item)
            },
          })
        },
      }
    : {}

  return h(
    tableComponent,
    mergeProps(props, context.attrs, {
      fields: selection ? [tableField(FIELD_NAME)].concat(fields) : fields,
      items: items,
      aggregateFields,
      aggregate,
    }),
    Object.assign(slots, context.slots)
  )
}
